import { useLayoutEffect, useRef } from "react";
import { cn } from "@/lib/utils";

interface Props {
  // Reports the bar's rendered height so the grid below can offset its own
  // sticky header rows to stack under this bar.
  onHeightChange: (height: number) => void;
  children: React.ReactNode;
  className?: string;
}

export default function StickyBar({ onHeightChange, children, className }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const report = () => onHeightChange(el.getBoundingClientRect().height);
    report();
    const observer = new ResizeObserver(report);
    observer.observe(el);
    return () => observer.disconnect();
  }, [onHeightChange]);

  return (
    <div
      ref={ref}
      // flow-root keeps children's bottom margin inside the measured height,
      // so the opaque background covers the full gap above the grid header.
      // z-40 beats the grid's sticky header stack (z-30 corners) but stays
      // below the app overlay layer (dropdowns/popovers/dialogs at z-50).
      className={cn("flow-root sticky top-0 z-40 px-3 pt-3", className)}
      // The 1px bg-colored shadow covers the sub-pixel seam that can open
      // between the bar and the stuck grid header at fractional scroll offsets.
      style={{ background: "var(--background)", boxShadow: "0 1px 0 0 var(--background)" }}
    >
      {children}
    </div>
  );
}
